'use client'

import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { useBatchedStreamCallback } from '@/hooks/use-batched-stream-callback'
import { useActiveAccountsScope } from '@/service/account.hook'
import { useResyncDumpTracker } from '@/service/runtime-health/use-resync-dump-tracker'
import { applyPositionFrames, type PositionsFrame } from '@/service/ws/merge-positions'
import { type AccountScopedResponse, usePositionsStream } from '@/service/ws/use-orders-stream'
import { accountDataLoadingAtom, positionsRawAtom, triggerResyncAtom } from '@/state/account'
import type { PositionList } from '@/types/generated/common/trading'
import { AccountDataStreamType } from '@/types/generated/common/types'

const POSITIONS_FLUSH_INTERVAL_MS = 100

/**
 * Owns every active account's positions subscription on a single batched
 * channel (`accountIds: [...]` instead of one channel per account).
 *
 * Snapshot-vs-delta comes from the server's `isFullSnapshot`, never from a local
 * "first frame since reset" guess. Deltas are field-level (a row whose size
 * didn't move arrives without a quantity), so mistaking one for a snapshot wipes
 * those rows — and a recovery resync resets any local guess while frames are
 * still in flight, which made positions vanish after returning to the tab. The
 * dump tracker is kept only to ack the resync once every account re-snapshotted.
 */
function AccountPositionsCoordinator() {
  const { accountIds, scopeKey, isResolved, hasAccounts } = useActiveAccountsScope()
  const setPositions = useSetAtom(positionsRawAtom)
  const setLoading = useSetAtom(accountDataLoadingAtom)
  const triggerResync = useAtomValue(triggerResyncAtom)
  const { isAuthenticated } = useAuth()

  const { trackerRef, syncGeneration, noteDumped, resetTracker } = useResyncDumpTracker('positions')
  const prevIsAuthenticatedRef = useRef(isAuthenticated)
  const prevAccountIdsRef = useRef<string[]>([])

  // A fresh subscribe (scope change, resync, or reconnect) means the server
  // re-sends a snapshot for every account in the new channel — reset the
  // tracker so the resync is acked only once all of them have landed.
  useEffect(() => {
    resetTracker()
  }, [scopeKey, triggerResync, resetTracker])

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current
    prevIsAuthenticatedRef.current = isAuthenticated
    if (!wasAuthenticated && isAuthenticated) {
      resetTracker()
    }
  }, [isAuthenticated, resetTracker])

  // Prune accounts that dropped out of scope.
  useEffect(() => {
    if (!isResolved) return
    const dropped = prevAccountIdsRef.current.filter((id) => !accountIds.includes(id))
    if (dropped.length > 0) {
      const droppedSet = new Set(dropped)
      setPositions((prev) => prev.filter((p) => !droppedSet.has(p.accountId)))
    }
    prevAccountIdsRef.current = accountIds
  }, [accountIds, isResolved, setPositions])

  useEffect(() => {
    if (isResolved && !hasAccounts) {
      setPositions([])
    }
  }, [isResolved, hasAccounts, setPositions])

  const positionsPayload = useMemo(
    () => ({
      accountId: '',
      accountIds,
      dataType: AccountDataStreamType.ACCOUNT_DATA_STREAM_TYPE_POSITIONS,
    }),
    [accountIds]
  )

  // The hub re-stamps marks several times a second per account; one atom write
  // per window caps the positions-table render rate.
  const applyFrames = useCallback(
    (frames: readonly PositionsFrame[]) =>
      setPositions((prev) => applyPositionFrames(prev, frames)),
    [setPositions]
  )
  const { enqueue, flush } = useBatchedStreamCallback(applyFrames, {
    intervalMs: POSITIONS_FLUSH_INTERVAL_MS,
    resetKey: scopeKey,
  })

  const handlePositionsMessage = useCallback(
    (response: AccountScopedResponse<PositionList> | undefined) => {
      if (!response?.data.positions) return
      const { accountId, data } = response

      // Before markDumped: a resync's fresh snapshot can beat the effect that
      // resets the tracker, and would otherwise be credited to the old generation.
      syncGeneration()

      // The stream hook already guarantees this message's envelope accountId
      // matches ours, so any item still missing its own accountId safely belongs
      // to this account too (the server doesn't reliably echo it per item).
      const taggedPositions = data.positions.map((p) => (p.accountId ? p : { ...p, accountId }))

      const { isFullSnapshot } = data
      enqueue([{ accountId, positions: taggedPositions, isFullSnapshot }])
      if (!isFullSnapshot) return

      // A snapshot lands synchronously so the loading flag never clears ahead of
      // the rows it announces.
      flush()
      trackerRef.current.markDumped(accountId)
      noteDumped(accountIds)
      setLoading((prev) => ({ ...prev, positions: false }))
    },
    [enqueue, flush, setLoading, syncGeneration, noteDumped, trackerRef, accountIds]
  )

  usePositionsStream({
    connected: true,
    payload: positionsPayload,
    onMessage: handlePositionsMessage,
  })

  return null
}

/**
 * Mount once, alongside `<AccountDataManager />`.
 */
export function ActivePositionsSync() {
  return <AccountPositionsCoordinator />
}
